// components/TemplateSelector.jsx
import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Badge, Spinner, Alert, Button, Form } from 'react-bootstrap';
import { FaRegFilePdf, FaCheck, FaStar, FaCrown } from 'react-icons/fa';
import { getAvailableTemplates } from '../services/templateService';
import './TemplateSelector.css';

function TemplateSelector({
  selectedTemplateId,
  onTemplateSelect,
  exportFormat = 'pdf',
  showManageButton = false,
  onManageClick,
  hideCustomTemplates = false
}) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadTemplates();
  }, [hideCustomTemplates]);

  const loadTemplates = async () => {
    setLoading(true);
    setError(null);

    try {
      const fetchedTemplates = await getAvailableTemplates();

      console.log('[TemplateSelector] Fetched templates:', fetchedTemplates.length, fetchedTemplates);

      // Remove duplicates based on template name (case-insensitive)
      // Keep the first occurrence of each template name
      const seenNames = new Set();
      let uniqueTemplates = fetchedTemplates.filter(template => {
        const normalizedName = template.name?.toLowerCase().trim();
        if (!normalizedName || seenNames.has(normalizedName)) {
          return false; // Skip duplicate
        }
        seenNames.add(normalizedName);
        return true; // Keep unique template
      });

      console.log('[TemplateSelector] Unique templates after deduplication:', uniqueTemplates.length, uniqueTemplates);

      // Note: Backend already handles filtering custom templates based on Build-Your-Own-Reports feature status
      // When feature is disabled, backend only returns user-created templates
      // When feature is enabled, backend returns all tenant templates
      // So we trust the backend response and don't filter here
      // The hideCustomTemplates prop is kept for backward compatibility but filtering is done on backend

      setTemplates(uniqueTemplates);

      // Auto-select first template if none selected
      if (!selectedTemplateId && uniqueTemplates.length > 0) {
        onTemplateSelect(uniqueTemplates[0].id);
      }
    } catch (err) {
      setError(err.message);
      console.error('Error loading templates:', err);
    } finally {
      setLoading(false);
    }
  };

  const getTemplateIcon = (template) => {
    if (template.is_default) {
      return <FaCrown className="text-warning" />;
    }
    if (template.isCustom || String(template.id).startsWith('custom_')) {
      return <FaStar className="text-success" />;
    }
    return <FaStar className="text-primary" />;
  };

  const getTemplateLabel = (template) => {
    if (template.id === 1) return 'Professional';
    if (template.id === 2) return 'Executive';
    if (template.is_default) return 'Default';
    if (template.isCustom || String(template.id).startsWith('custom_')) {
      return 'ReportBuilder';
    }
    return 'Custom';
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3 text-muted">Loading templates...</p>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger">
        <Alert.Heading>Error Loading Templates</Alert.Heading>
        <p>{error}</p>
        <Button variant="outline-danger" size="sm" onClick={loadTemplates}>
          Retry
        </Button>
      </Alert>
    );
  }

  if (templates.length === 0) {
    return (
      <Alert variant="info">
        <p className="mb-0">No templates available. Please contact your administrator.</p>
      </Alert>
    );
  }

  return (
    <div className="template-selector">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6 className="mb-0">
          <FaRegFilePdf className="me-2" />
          Select Report Template
        </h6>
        {/* {showManageButton && (
          <Button 
            variant="outline-primary" 
            size="sm"
            onClick={onManageClick}
          >
            Manage Templates
          </Button>
        )} */}
      </div>

      <Row className="g-3">
        {templates.map((template) => (
          <Col key={template.id} xs={12} md={6} lg={4}>
            <Card
              className={`template-card ${selectedTemplateId === template.id ? 'selected' : ''}`}
              onClick={() => onTemplateSelect(template.id)}
              style={{ cursor: 'pointer' }}
            >
              <Card.Body>
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <div className="template-icon">
                    {getTemplateIcon(template)}
                  </div>
                  {selectedTemplateId === template.id && (
                    <Badge bg="success" className="template-selected-badge">
                      <FaCheck className="me-1" />
                      Selected
                    </Badge>
                  )}
                </div>

                <Card.Title className="template-name">
                  {template.name}
                </Card.Title>

                <Card.Text className="template-description text-muted small">
                  {template.description || 'No description available'}
                </Card.Text>

                <div className="template-meta mt-2">
                  <Badge
                    bg={template.is_default ? 'warning' : 'info'}
                    className="me-2"
                  >
                    {getTemplateLabel(template)}
                  </Badge>
                  <Badge bg="secondary">
                    {template.base_template || 'template1'}
                  </Badge>
                </div>

                {/* Configuration preview for custom templates */}
                {!template.is_default && template.configuration && (
                  <div className="template-config-preview mt-2">
                    <small className="text-muted d-block">
                      {template.configuration.content?.coverPageTitle && (
                        <span className="d-block text-truncate">
                          📄 {template.configuration.content.coverPageTitle}
                        </span>
                      )}
                      {template.configuration.colors?.primary && (
                        <span className="d-flex align-items-center mt-1">
                          🎨
                          <span
                            className="color-swatch ms-2"
                            style={{
                              backgroundColor: template.configuration.colors.primary,
                              display: 'inline-block',
                              width: '16px',
                              height: '16px',
                              borderRadius: '3px',
                              border: '1px solid #dee2e6'
                            }}
                          />
                        </span>
                      )}
                    </small>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {/* <div className="template-info mt-3">
        <small className="text-muted">
          💡 <strong>Tip:</strong> Custom templates allow you to brand reports with your company colors and content.
          {templates.filter(t => !t.is_default).length === 0 && (
            <span> Click "Manage Templates" to create your first custom template.</span>
          )}
        </small>
      </div> */}
    </div>
  );
}

export default TemplateSelector;

